import { type LineItemType } from "legacyComponents/LineItemContainer.types";
import { PureComponent } from "react";
import { withRouter } from "react-router";
import { type RouteComponentProps } from "react-router-dom";
import { Blue7 } from "styles/antTheme/Colors";

import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";
import { cn } from "@btnet/utilities-core";

import { BdsIconSparkle } from "@buildertrend/components";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { CostType } from "types/enum";

import { getDefaultMarkupMarginForCostCostCodeCostType } from "utilities/lineItem/lineItemHelper";
import { recalculateAllFields } from "utilities/lineItem/markupUtility";
import { isNullOrUndefined } from "utilities/object/object";
import { routes } from "utilities/routes";
import { findScrollableParents } from "utilities/scroll/scroll";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconCoins, BTIconLayers } from "commonComponents/btWrappers/BTIcon";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { CostCodeSelector } from "commonComponents/entity/costCode/CostCodeSelector/CostCodeSelector";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import { type IInputDebouncingContext } from "commonComponents/utilities/Input/InputDebouncingContext";
import { LinkedCostItemIcon } from "commonComponents/utilities/LineItemContainer/cellImplementations/TitleAndCostCodeCell/LinkedCostItemIcon";
import {
    getLineItemDataFromSearchResult,
    type IPreviewLineItemData,
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemColumn } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IBaseLineItem,
    type ICostCodeCostTypesMarkup,
    type IMarkupLineItem,
    LineItemColumns,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { ResizableSearchBar } from "commonComponents/utilities/MainNavigation/searchLegacy/ResizableSearchBar";
import { SearchCategory } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";
import { RequiredFieldIndicator } from "commonComponents/utilities/RequiredFieldIndicator/RequiredFieldIndicator";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import { type ICostTypesMarkup } from "entity/job/Job.api.types";
import { CostCodeTaxableKey } from "entity/tax/common/tax.types";
import { type VendorTypes } from "entity/vendor/common/vendor.types";
import VendorProductLabel from "entity/vendor/VendorProductLabel/VendorProductLabel";

import "./TitleAndCostCodeCell.less";

import { NoTaxId } from "../../../../../entity/tax/TaxRate/TaxRate.api.types";

export interface ITitleAndCostCodeProps extends IPreviewLineItemProps {
    costItemId?: number | null;
    jobId?: number;
    lineItemType: LineItemType;
    shouldShowLink: boolean;
    /**
     * When provided (and `shouldShowLink` is set), the title renders as a link-styled button
     * that calls this instead of navigating. Used where the target is a split-view panel rather
     * than a route - there is no URL to navigate to, so rendering a real anchor would expose a
     * misleading href to middle-click and "open in new tab". Leave undefined to keep the
     * normal route link.
     */
    onTitleClick?: () => void;
    vendorProductId?: number | null;
    vendorType?: VendorTypes | null;
    isDiscontinued?: boolean | null;
    costCodeProps: ICostCodeProps;
    titleProps: ITitleProps;
    ellipsis?: boolean;
    onPreviewLineItemSet?: (lineItem?: IPreviewLineItemData) => void;
    onLineItemSelected?: (lineItem: IPreviewLineItemData, parentId?: number) => void;
    parentId?: number;
    isStackedLineItem?: boolean;
    debouncingContext?: IInputDebouncingContext;
    selectionOptionGroupId?: number | null;
}

export interface ITitleAndCostCodeCellWithMarkupProps
    extends ITitleAndCostCodeProps,
        Omit<IMarkupLineItem, "isDiscontinued" | "costItemId" | "vendorType" | "vendorProductId"> {
    id: number;
    costTypesMarkup: ICostTypesMarkup[];
    costCodeCostTypesMarkup?: ICostCodeCostTypesMarkup[];
}

export interface ICostCodeProps {
    costCodeId: number | null;
    costCodes?: BTSelectItem<any>[];
    costCodeTitle?: string | null;
    canEditCostCode: boolean;
    costCodeEditing?: ICostCodeEditing;
    hasPredictedCostCode?: boolean;
}

export interface ITitleProps {
    canEditTitle: boolean;
    lineItemTitle?: string | null;
    enableCostItemSearch?: boolean;
}

export interface ICostCodeEditing {
    canEdit?: boolean;
    onListChange: (costCodes: BTSelectItem<any>[]) => void;
    hasCostCodesFeature: boolean;
}

class TitleAndCostCodeCellInternal extends PureComponent<
    IGridCellRenderProps<ITitleAndCostCodeProps | ITitleAndCostCodeCellWithMarkupProps> &
        RouteComponentProps
> {
    private handleCostCodeListChange = (costCodes: BTSelectItem<any>[]) => {
        this.props.cellValue?.costCodeProps.costCodeEditing?.onListChange?.(costCodes);
    };

    private handleCostCodeChange = (field: string, value: any) => {
        const { cellValue, onChange } = this.props;
        const costCodeId = Array.isArray(value) ? value[0] : (value as number);
        if (
            cellValue &&
            "id" in cellValue &&
            cellValue.id <= 0 &&
            cellValue.costTypesMarkup.length > 0
        ) {
            const { markupPercent, margin, _markupSource } =
                getDefaultMarkupMarginForCostCostCodeCostType(
                    cellValue.costTypesMarkup,
                    cellValue.costTypes,
                    value,
                    cellValue.costCodeCostTypesMarkup,
                )[0];
            const markupType = (margin ?? 0) > 0 ? MarkupColumnTypes.Margin : cellValue.markupType;

            const calculatedFields = recalculateAllFields({
                ...cellValue,
                markupType,
                markupPercent,
                margin,
            } as IMarkupLineItem);
            onChange(this.props.field.path, {
                ...cellValue,
                ...calculatedFields,
                _markupSource,
                costCodeId: value,
            });
        } else {
            onChange(field, value);
        }
        const costCodeExtraData = this.props.cellValue?.costCodeProps.costCodes?.find(
            (x) => Number(x.id) === costCodeId,
        )?.extraData;

        if (
            !isNullOrUndefined(costCodeExtraData) &&
            !isNullOrUndefined(costCodeExtraData[CostCodeTaxableKey]) &&
            !costCodeExtraData[CostCodeTaxableKey]
        ) {
            this.props.onChange(`${this.props.field.path}.taxGroupId`, NoTaxId);
        }
    };

    private getTitleField = (preview?: IPreviewLineItemData) => {
        if (!this.props.cellValue) {
            return null;
        }

        const { onChange, onBlur, autoFocus, field, isInEdit } = this.props;
        const {
            ellipsis,
            onPreviewLineItemSet,
            onLineItemSelected,
            isStackedLineItem,
            selectionOptionGroupId,
        } = this.props.cellValue;

        const { enableCostItemSearch, lineItemTitle, canEditTitle } =
            this.props.cellValue.titleProps;

        const isReadOnly = !isInEdit || !canEditTitle;
        const titleFieldName = field.path + ".itemTitle";
        const value = preview?.title ?? lineItemTitle ?? undefined;

        if (isStackedLineItem && isReadOnly) {
            return (
                <ValueDisplay
                    id={titleFieldName}
                    data-testid={titleFieldName}
                    value={
                        <>
                            <BTIconLayers style={{ color: Blue7 }} /> {value}
                        </>
                    }
                />
            );
        }

        let lineItemTitleField: React.ReactNode;
        if (!isReadOnly && enableCostItemSearch) {
            lineItemTitleField = (
                <>
                    {/* If the selectionOptionGroupId is not null, show the allowance icon */}
                    {selectionOptionGroupId && (
                        <BTIconCoins
                            data-testid="allowance-item-icon"
                            className="margin-top-xss margin-left-xss"
                        />
                    )}
                    <TitleSearch
                        titleFieldName={titleFieldName}
                        onPreviewLineItemSet={onPreviewLineItemSet}
                        onLineItemSelected={onLineItemSelected}
                        className={preview ? "LineItemPreview" : undefined}
                        onChange={onChange}
                        value={value}
                        parentId={this.props.cellValue.parentId}
                        // Only true for a row the user just explicitly added (not on page load),
                        // so the usual autofocus-disorients-screen-readers concern doesn't apply.
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={autoFocus}
                    />
                </>
            );
        } else {
            lineItemTitleField = (
                <>
                    <BTRow>
                        {/* If the selectionOptionGroupId is not null, show the allowance icon */}
                        {selectionOptionGroupId && (
                            <BTIconCoins
                                data-testid="allowance-item-icon"
                                className="margin-top-xss margin-left-xss margin-right-xs"
                            />
                        )}
                        <BTInput<undefined>
                            id={titleFieldName}
                            data-testid={titleFieldName}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            readOnly={isReadOnly}
                            // Only true when the user explicitly puts this row into edit mode,
                            // not on page load - see the TitleSearch branch above for more.
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={autoFocus}
                            disableFormSubmit
                            className={cn({ ellipsis: ellipsis })}
                            debouncingContext={this.props.cellValue.debouncingContext}
                        />
                    </BTRow>
                </>
            );
        }

        return lineItemTitleField;
    };

    private getCostCodeField = (preview?: IPreviewLineItemData) => {
        if (!this.props.cellValue) {
            return null;
        }
        const { rowIndex, isInEdit, field } = this.props;
        const { ellipsis } = this.props.cellValue;
        const { costCodes, costCodeId, costCodeTitle, costCodeEditing, canEditCostCode } =
            this.props.cellValue.costCodeProps;

        const costCodeFieldName = field.path + ".costCodeId";

        let costCodeField: JSX.Element;
        if (!!costCodeTitle) {
            costCodeField = (
                <ValueDisplay
                    id={costCodeFieldName}
                    data-testid={costCodeFieldName}
                    value={preview?.costCodeTitle ?? costCodeTitle}
                    className={preview ? "LineItemPreview" : undefined}
                />
            );
        } else {
            const costCodeComponent = (
                <CostCodeSelector<IBaseLineItem[]>
                    id={costCodeFieldName as keyof IBaseLineItem[]}
                    data-testid={costCodeFieldName}
                    costCodes={costCodes ?? []}
                    onChange={this.handleCostCodeChange}
                    onBlur={() => {}}
                    selectedCostCodeID={preview?.costCodeId ?? (costCodeId as number)}
                    readOnly={!isInEdit || !canEditCostCode}
                    canEdit={!!costCodeEditing?.canEdit}
                    canAdd={!!costCodeEditing?.canEdit}
                    costType={CostType.Cost}
                    index={rowIndex}
                    onCostCodeChange={(costCodes) => {
                        if (costCodes.length > 0) {
                            const budgeted = costCodes.find(
                                (x) => x.title === "Budgeted cost codes",
                            );
                            const other = costCodes.find((x) => x.title === "Other cost codes");
                            const rest = costCodes.filter((x) => x !== budgeted && x !== other);
                            const sortedRest = [...rest].sort((a, b) =>
                                a.title.localeCompare(b.title),
                            );
                            if (budgeted) sortedRest.push(budgeted);
                            if (other) sortedRest.push(other);
                            this.handleCostCodeListChange(sortedRest);
                        } else {
                            this.handleCostCodeListChange(costCodes);
                        }
                    }}
                    isInEdit={isInEdit}
                    hasCostCodesFeature={!!costCodeEditing?.hasCostCodesFeature}
                    disableDelete
                    className={preview ? "LineItemPreview" : undefined}
                />
            );

            costCodeField = <div className={cn({ ellipsis: ellipsis })}>{costCodeComponent}</div>;
        }

        return costCodeField;
    };

    render() {
        const { isInEdit, canEdit, field, onFocus, entityId } = this.props;
        const { hasPredictedCostCode } = this.props.cellValue?.costCodeProps ?? {};

        const titleFieldName = field.path + ".itemTitle";
        const costCodeFieldName = field.path + ".costCodeId";

        if (!this.props.cellValue) {
            return null;
        }

        const {
            costItemId,
            shouldShowLink,
            vendorProductId,
            vendorType,
            isDiscontinued,
            onTitleClick,
        } = this.props.cellValue;

        const { canEditCostCode } = this.props.cellValue.costCodeProps;
        const { canEditTitle } = this.props.cellValue.titleProps;

        const lineItemTitleField = (
            <LineItemPreview
                fieldName="title"
                addPreviewListener={this.props.cellValue.addPreviewListener}
                render={this.getTitleField}
            />
        );

        const costCodeField = (
            <LineItemPreview
                fieldName="costCode"
                addPreviewListener={this.props.cellValue.addPreviewListener}
                render={this.getCostCodeField}
            />
        );

        if (canEdit && isInEdit) {
            return (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-row">
                        <BTFormItemAutomatic
                            id={titleFieldName}
                            className="TitleAndCostCodeInput bds-grow"
                        >
                            {lineItemTitleField}
                        </BTFormItemAutomatic>
                        <LinkedCostItemIcon costItemId={costItemId} marginTop="xs" paddingTop />
                    </div>
                    <BTFormItemAutomatic
                        id={costCodeFieldName}
                        label={({ label, isRequiredFieldIndicatorVisible }) => (
                            <>
                                <span>{label}</span>
                                {isRequiredFieldIndicatorVisible && <RequiredFieldIndicator />}
                                {hasPredictedCostCode && (
                                    <BdsIconSparkle size="sm" className="icon " />
                                )}
                            </>
                        )}
                    >
                        {costCodeField}
                    </BTFormItemAutomatic>
                </div>
            );
        }

        let linkComponent: JSX.Element;
        if (shouldShowLink) {
            const titleAndCostCodeContent = (
                <>
                    <div className="flex">
                        {canEditTitle && isInEdit ? (
                            <BTFormItemAutomatic id={titleFieldName}>
                                {lineItemTitleField}
                            </BTFormItemAutomatic>
                        ) : (
                            <b>{lineItemTitleField}</b>
                        )}
                        <LinkedCostItemIcon costItemId={costItemId} marginTop="xss" />
                    </div>
                    {canEditCostCode && isInEdit ? (
                        <BTFormItemAutomatic id={costCodeFieldName}>
                            {costCodeField}
                        </BTFormItemAutomatic>
                    ) : (
                        <div>{costCodeField}</div>
                    )}
                </>
            );

            linkComponent = (
                <div className="TitleAndCostCodeReadonlyCell">
                    {onTitleClick ? (
                        <BTButton
                            type="link"
                            data-testid="titleAndCostCodeButton"
                            className="TitleAndCostCodeButton"
                            onClick={onTitleClick}
                        >
                            {titleAndCostCodeContent}
                        </BTButton>
                    ) : (
                        <BTLinkRelative
                            to={routes.estimate.getLineItemDetail(
                                entityId ?? 0,
                                this.props.cellValue.jobId ?? 0,
                                this.props.cellValue.lineItemType,
                            )}
                            tabIndex={-1}
                            isUnderline={false}
                        >
                            {titleAndCostCodeContent}
                        </BTLinkRelative>
                    )}
                </div>
            );
        } else {
            linkComponent = (
                <div>
                    <div className="flex">
                        {canEditTitle && isInEdit ? (
                            <BTFormItemAutomatic id={titleFieldName}>
                                {lineItemTitleField}
                            </BTFormItemAutomatic>
                        ) : (
                            <b>{lineItemTitleField}</b>
                        )}
                        <LinkedCostItemIcon costItemId={costItemId} marginTop="xss" />
                    </div>
                    {canEditCostCode && isInEdit ? (
                        <BTFormItemAutomatic id={costCodeFieldName}>
                            {costCodeField}
                        </BTFormItemAutomatic>
                    ) : (
                        <div className="flex flex-row gap-1 flex-wrap">
                            {costCodeField}
                            {hasPredictedCostCode && (
                                <BTStatusBadge
                                    testid="auto-filled-badge"
                                    text="Auto-filled"
                                    displayType="info"
                                    icon={<BdsIconSparkle size="sm" />}
                                />
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                className={cn({
                    lineItemEditableCell: !isInEdit && canEdit && (canEditCostCode || canEditTitle),
                    TitleAndCostCodeReadonlyCell: !isInEdit,
                })}
                tabIndex={canEdit ? 0 : -1}
                onFocus={!isInEdit ? onFocus : () => {}}
            >
                <ShowOnPortal
                    builder={!!vendorProductId}
                    render={() => (
                        <VendorProductLabel
                            vendorProductId={vendorProductId!}
                            vendorType={vendorType!}
                            isDiscontinued={isDiscontinued!}
                            showIcon
                            showPopover
                        />
                    )}
                />
                {linkComponent}
            </div>
        );
    }
}

export const TitleAndCostCodeCell = withRouter(TitleAndCostCodeCellInternal);

export const titleAndCostCodeCellDefaultConfig = <T extends IBaseLineItem>() =>
    ({
        id: LineItemColumns.TitleAndCostCode,
        name: "Items",
        jsonKey: "titleAndCostCode",
        align: "left",
        isSortable: true,
        columnType: GridLineItemColumnType.titleAndCostCode,
        isFrozen: true,
        isHidden: false,
        Cell: TitleAndCostCodeCell,
        getFooterValue: (_) => "Totals",
        "data-testid": "titleAndCostCode",
    }) as Partial<ILineItemColumn<T, ITitleAndCostCodeProps>>;

interface ITitleSearchProps {
    titleFieldName: string;
    onPreviewLineItemSet?: (lineItem?: IPreviewLineItemData) => void;
    onLineItemSelected?: (lineItem: IPreviewLineItemData, parentId?: number) => void;
    onChange: (field: string, value: any) => void;
    value?: string;
    className?: string;
    parentId?: number;
    autoFocus?: boolean;
}

const TitleSearch: React.FC<ITitleSearchProps> = (props) => {
    return (
        <ResizableSearchBar
            isCollapsed={false}
            inputPadding={false}
            shouldAutoFillOnHover
            onResultItemClick={async (result) => {
                const lineItem = getLineItemDataFromSearchResult(result.data);
                props.onPreviewLineItemSet?.(undefined);
                props.onLineItemSelected?.(lineItem, props.parentId);
                return lineItem.title;
            }}
            presetSearchCategory={[SearchCategory.CostItems]}
            searchLimit={10}
            onValueChange={(val) => props.onChange(props.titleFieldName, val)}
            onResultItemHover={(result) => {
                props.onPreviewLineItemSet?.(
                    result ? getLineItemDataFromSearchResult(result.data) : undefined,
                );
            }}
            value={props.value}
            className={props.className}
            getCloseOnScrollTargets={(el) => findScrollableParents(el)}
            // Only true for a row the user just explicitly added (not on page load), so the
            // usual autofocus-disorients-screen-readers concern doesn't apply here.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={props.autoFocus}
        />
    );
};
